import { useMutation, useQueryClient } from "@tanstack/react-query";

import { ARTIFACTS_QUERY_KEY, type Artifact } from "@/api/artifacts";
import { ApiError, apiFetch } from "@/api/client";

// Portal structure upload (dispatch 039). POST /api/molecules is the HTTP
// surface for what `client.types.Molecule.from_sdf` does in-process:
// read + repair an uploaded structure file and seed the catalog. The picker
// calls this so a user can drop in a ligand without the SDK — the returned
// artifact is injected straight into the artifacts cache and selected.
// Wire-format mirrors src/flowstack/server/routes/molecules.py.

// Upper bound on a file we bother sending — kept in sync with the
// backend's MAX_UPLOAD_BYTES so a too-large file fails fast client-side
// with a clear message instead of round-tripping to a 413.
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

/**
 * The extensions the route accepts, mirroring `ACCEPTED_SUFFIXES` in
 * src/flowstack/server/routes/molecules.py. That set is AFFDO's own
 * (`AFFDO/src/iomod.py:2034-2044` `GetInputFormat`), not a webapp choice —
 * the archive ships ligands as `.mol`, and before flowstack#1319 each one
 * had to be renamed to `.sdf` by hand to get through this control.
 *
 * Lower-case, leading dot: compared against a filename's own suffix
 * lower-cased, exactly as the server does.
 */
export const ACCEPTED_MOLECULE_SUFFIXES = [".sdf", ".mol", ".mol2", ".pdb"] as const;

/** Value for a file input's `accept` attribute. */
export const MOLECULE_ACCEPT_ATTRIBUTE = ACCEPTED_MOLECULE_SUFFIXES.join(",");

/**
 * Whether `filename` carries an accepted extension.
 *
 * `.mol` is a suffix of neither `.mol2` nor anything else here, but the
 * comparison is on the last dot-segment rather than `endsWith` anyway, so a
 * file called `ligand.notmol` cannot sneak through.
 */
export function hasAcceptedMoleculeSuffix(filename: string): boolean {
  const dot = filename.lastIndexOf(".");
  if (dot < 0) return false;
  const suffix = filename.slice(dot).toLowerCase();
  return (ACCEPTED_MOLECULE_SUFFIXES as readonly string[]).includes(suffix);
}

export interface UploadMoleculeRequest {
  file: File;
  /** Formal charge the user asserts for the molecule. */
  declaredCharge: number;
}

/**
 * Response from POST /api/molecules — shaped as an {@link Artifact}
 * (plus `created`) so it can merge into the `["artifacts","list"]`
 * cache and be selected by the picker with no refetch. `created` is
 * false when the upload deduped onto a catalog row that already existed.
 */
export interface UploadMoleculeResponse extends Artifact {
  created: boolean;
}

export type UploadMoleculeErrorCode =
  | "unsupported_file_type"
  | "multi_record_file"
  | "empty_upload"
  | "file_too_large"
  | "molecule_ingest_failed"
  | "database_not_configured"
  | "object_store_not_configured"
  | "validation_error";

/**
 * Upload a structure file and seed it as a catalog Molecule. On success
 * the seeded artifact is written into the artifacts query cache (so the
 * picker shows it immediately) and the list is invalidated so a later
 * poll reconciles with the server.
 */
export function useUploadMolecule() {
  const queryClient = useQueryClient();
  return useMutation<UploadMoleculeResponse, Error, UploadMoleculeRequest>({
    mutationFn: async ({ file, declaredCharge }): Promise<UploadMoleculeResponse> => {
      // Refuse an unaccepted extension before sending. The `accept`
      // attribute on the input is a filter, not a guard — a user can pick
      // "All Files" in the OS dialog past it — and the server dispatches on
      // the suffix, so an unnamed-format upload is a wasted round trip with
      // a worse error. Same code + status the server returns.
      if (!hasAcceptedMoleculeSuffix(file.name)) {
        throw new ApiError(400, {
          error_code: "unsupported_file_type",
          message: `molecule upload accepts ${ACCEPTED_MOLECULE_SUFFIXES.join(", ")}; got ${file.name}.`,
        });
      }
      // Enforce the advertised client-side size guard: fail fast before
      // building the request body so an oversized file gets an immediate,
      // clear error instead of streaming the whole thing only to get a 413
      // back (Rupert / Copilot PR-513). Thrown as an ApiError with the
      // same `file_too_large` code + 413 status the server would return,
      // so downstream error handling can't tell the two paths apart.
      if (file.size > MAX_UPLOAD_BYTES) {
        throw new ApiError(413, {
          error_code: "file_too_large",
          message: `the upload is ${file.size} bytes; the limit is ${MAX_UPLOAD_BYTES} bytes.`,
        });
      }
      const form = new FormData();
      form.append("file", file);
      form.append("declared_charge", String(declaredCharge));
      // apiFetch forwards `body` verbatim and omits Content-Type when
      // there's no `json`, so the browser sets the multipart boundary.
      return apiFetch<UploadMoleculeResponse>("/api/molecules", {
        method: "POST",
        body: form,
      });
    },
    onSuccess: (artifact) => {
      // Merge the seeded artifact into the cached list so the picker can
      // select it right away (a run-less upload won't appear until the
      // server-side list refetch otherwise, and dedup means it may not
      // change the list at all — the cache write is what makes it usable).
      queryClient.setQueryData<Artifact[]>(ARTIFACTS_QUERY_KEY, (prev) => {
        const rest = (prev ?? []).filter((a) => a.content_hash !== artifact.content_hash);
        return [artifact, ...rest];
      });
      queryClient.invalidateQueries({ queryKey: ARTIFACTS_QUERY_KEY });
    },
  });
}
